/**
 * GPX/KML 导出与路线推送队员
 */
import { useMemo, useState } from 'react';
import { message } from '../utils/antdMessage';
import apiFetch from '../utils/apiFetch';
import { buildGpx, buildKml, downloadText } from '../utils/mapDrawingBoard';

export default function useMapDrawingExport({
  members = [],
  allowPush = true,
  onTrackPushed,
  setCtxMenu
}) {
  const [pushOpen, setPushOpen] = useState(false);
  const [pushFeature, setPushFeature] = useState(null);
  const [pushForm, setPushForm] = useState({
    org_names: [],
    group_names: [],
    user_names: [],
    message: '',
    to_all: false
  });
  const [pushing, setPushing] = useState(false);

  const memberMeta = useMemo(() => {
    const orgSet = new Set();
    const groups = [];
    const users = [];
    const seenGroup = new Set();
    const seenUser = new Set();
    (members || []).forEach((d) => {
      const org = d.org_name || '未分组';
      const group = d.group_name || '未分组';
      const name = d.name || d.device_unique_id || '未知';
      orgSet.add(org);
      const gk = `${org}::${group}`;
      if (!seenGroup.has(gk)) {
        seenGroup.add(gk);
        groups.push({ name: group, org });
      }
      if (!seenUser.has(name)) {
        seenUser.add(name);
        users.push({ name, org, group });
      }
    });
    return {
      organizations: Array.from(orgSet).sort(),
      groups: groups.sort((a, b) => a.name.localeCompare(b.name)),
      users: users.sort((a, b) => a.name.localeCompare(b.name))
    };
  }, [members]);

  const filteredGroups = pushForm.org_names.length
    ? memberMeta.groups.filter((g) => pushForm.org_names.includes(g.org))
    : [];
  const filteredUsers = (() => {
    let list = memberMeta.users;
    if (pushForm.org_names.length) {
      list = list.filter((u) => pushForm.org_names.includes(u.org));
    }
    if (pushForm.group_names.length) {
      list = list.filter((u) => pushForm.group_names.includes(u.group));
    }
    return list;
  })();

  const openPush = (feature) => {
    if (!allowPush) {
      message.warning('无「指挥系统-画板推送」权限');
      return;
    }
    setPushFeature(feature);
    setPushForm({
      org_names: [],
      group_names: [],
      user_names: [],
      message: feature?.name || '指挥系统画板路线',
      to_all: false
    });
    setPushOpen(true);
    setCtxMenu(null);
  };

  const doExport = (feature, format) => {
    if (!feature) return;
    const safe = String(feature.name || 'route').replace(/[\\/:*?"<>|]/g, '_');
    if (format === 'gpx') {
      downloadText(`${safe}.gpx`, buildGpx(feature), 'application/gpx+xml');
    } else {
      downloadText(`${safe}.kml`, buildKml(feature), 'application/vnd.google-earth.kml+xml');
    }
    message.success(`已导出 ${format.toUpperCase()}`);
    setCtxMenu(null);
  };

  const submitPush = async () => {
    if (!pushFeature || pushFeature.coordinates.length < 2) {
      message.error('路线无效');
      return;
    }
    if (!pushForm.to_all &&
      !pushForm.org_names.length &&
      !pushForm.group_names.length &&
      !pushForm.user_names.length) {
      message.warning('请选择接收者，或勾选推送给所有人');
      return;
    }
    setPushing(true);
    try {
      const coordinates = pushFeature.coordinates.map(([lng, lat]) => ({ lng, lat }));
      const body = {
        coordinates,
        file_type: 'gpx',
        file_name: `${pushFeature.name || 'board-route'}.gpx`,
        message: pushForm.message || pushFeature.name || '画板路线',
        icon: '旗帜',
        to_all: !!pushForm.to_all,
        org_names: pushForm.to_all ? [] : pushForm.org_names,
        group_names: pushForm.to_all ? [] : pushForm.group_names,
        user_names: pushForm.to_all ? [] : pushForm.user_names
      };
      const res = await apiFetch('/api/rescue/track', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) {
        const tip = data.detail ? `${data.error || '推送失败'}（${data.detail}）` : (data.error || '推送失败');
        message.error(tip);
        return;
      }
      message.success('路线已推送给队员');
      setPushOpen(false);
      onTrackPushed?.(data.track);
    } catch (err) {
      console.error(err);
      message.error('推送失败');
    } finally {
      setPushing(false);
    }
  };

  return {
    pushOpen,
    setPushOpen,
    pushFeature,
    pushForm,
    setPushForm,
    pushing,
    memberMeta,
    filteredGroups,
    filteredUsers,
    openPush,
    doExport,
    submitPush
  };
}
